import React from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertTitle, Card, FormControlLabel, Grid, Radio, RadioGroup, Stack, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useFormik, FormikProvider, Form } from 'formik';
import { LoadingButton } from '@mui/lab';
import dayjs from 'dayjs';

import { CustomerTypes } from '../../types';
import { CustomerService } from '../../services/customer.service';
import { RootState } from '../../store';
import { inviteCustomerFormSchema } from '../../schemas/customer.schema';
import { GT_COLORS } from '../../constants/designTokens.constants';

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, subtitle }) => (
  <Stack spacing={0.5}>
    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Stack>
);

export const InviteCustomerForm: React.FC = () => {
  const navigate = useNavigate();
  const { loading } = useSelector((state: RootState) => state.customersAdmin);

  const formik = useFormik<CustomerTypes.CreateCustomerParams>({
    initialValues: {
      organizationType: 'full',
      apiAccess: 'salesGated',
      // 30-day trial is the sales default; adjustable per deal.
      expiresAt: dayjs().add(30, 'day').format('MM/DD/YYYY'),
    } as CustomerTypes.CreateCustomerParams,
    validationSchema: inviteCustomerFormSchema,
    onSubmit: async (values) => {
      await CustomerService.create(values, (isValid, msg) => {
        toast(msg, { autoClose: 1500, type: isValid ? 'success' : 'error' });
        if (isValid) {
          formik.resetForm();
          navigate('/customers');
        }
      });
    },
  });

  const { handleSubmit, errors, touched, getFieldProps, values } = formik;

  const showError = (field: keyof CustomerTypes.CreateCustomerParams) =>
    Boolean((touched as any)[field] && (errors as any)[field]);

  const errorText = (field: keyof CustomerTypes.CreateCustomerParams) =>
    showError(field) ? (errors as any)[field] : undefined;

  const previewSlug = (values?.name || '').replace(/[^a-zA-Z0-9]/g, '');
  const isIraStandalone = values?.organizationType === 'iraStandalone';

  const handleExpiresAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    formik.setFieldValue('expiresAt', formatted);
  };

  return (
    <Card
      sx={{
        mt: 2,
        p: { xs: 2, md: 4 },
        border: `1px solid ${GT_COLORS.border}`,
        borderRadius: '12px',
        boxShadow: 'none',
      }}
    >
      <FormikProvider value={formik}>
        <Form noValidate onSubmit={handleSubmit}>
          <Stack spacing={4}>
            <Stack spacing={2}>
              <SectionHeader
                title="Organization Type"
                subtitle="What this organization gets access to. It can be changed later from Settings (v2)."
              />
              <RadioGroup
                row
                value={values?.organizationType ?? 'full'}
                onChange={(e) => formik.setFieldValue('organizationType', e.target.value)}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  label="Full Platform"
                />
                <FormControlLabel
                  value="iraStandalone"
                  control={<Radio />}
                  label="IRA Standalone — Intake Risk Assessment only, no SOS/devices/alerts"
                />
              </RadioGroup>
              {isIraStandalone && (
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600} color="text.primary">
                    API access
                  </Typography>
                  <RadioGroup
                    row
                    value={values?.apiAccess ?? 'salesGated'}
                    onChange={(e) => formik.setFieldValue('apiAccess', e.target.value)}
                  >
                    <FormControlLabel
                      value="salesGated"
                      control={<Radio />}
                      label="Sales-gated — docs visible, keys enabled by us"
                    />
                    <FormControlLabel
                      value="selfService"
                      control={<Radio />}
                      label="Self-service — org adds a card and generates keys, billed per search"
                    />
                  </RadioGroup>
                </Stack>
              )}
            </Stack>

            <Stack spacing={2}>
              <SectionHeader
                title="Contact and Company Information"
                subtitle="The point of contact will receive a Super Admin invitation at the email below."
              />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="Company Name"
                    placeholder="Acme Health"
                    required
                    {...getFieldProps('name')}
                    error={showError('name')}
                    helperText={errorText('name')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="Point of Contact First Name"
                    required
                    {...getFieldProps('contactFirstName')}
                    error={showError('contactFirstName')}
                    helperText={errorText('contactFirstName')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="Point of Contact Last Name"
                    required
                    {...getFieldProps('contactLastName')}
                    error={showError('contactLastName')}
                    helperText={errorText('contactLastName')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="medium"
                    type="email"
                    label="Point of Contact Email"
                    required
                    {...getFieldProps('email')}
                    error={showError('email')}
                    helperText={errorText('email')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="CC on invitation email (optional)"
                    placeholder="james@pomsafe.com, aj@pomsafe.com"
                    {...getFieldProps('ccEmails')}
                    error={showError('ccEmails')}
                    helperText={errorText('ccEmails') ?? 'Sales contacts copied on the owner invitation. Separate multiple addresses with commas.'}
                  />
                </Grid>
              </Grid>
            </Stack>

            <Stack spacing={2}>
              <SectionHeader
                title="Subscription Details"
                subtitle={isIraStandalone
                  ? 'IRA Standalone organizations have no mobile app licenses; only the expiration date applies.'
                  : 'Number of mobile app licenses and the subscription expiration date.'}
              />
              <Grid container spacing={2}>
                {!isIraStandalone && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="medium"
                      type="number"
                      label="Subscription License Count"
                      placeholder="10"
                      required
                      {...getFieldProps('licenseCount')}
                      error={showError('licenseCount')}
                      helperText={errorText('licenseCount')}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="Subscription Expiration Date"
                    placeholder="MM/DD/YYYY"
                    required
                    inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                    {...getFieldProps('expiresAt')}
                    onChange={handleExpiresAtChange}
                    error={showError('expiresAt')}
                    helperText={errorText('expiresAt')}
                  />
                </Grid>
              </Grid>
            </Stack>

            <Alert
              severity="info"
              sx={{
                borderRadius: '10px',
                border: `1px solid ${GT_COLORS.primaryLight}`,
                backgroundColor: GT_COLORS.primaryLight,
                color: GT_COLORS.textPrimary,
              }}
            >
              <AlertTitle sx={{ fontWeight: 600 }}>POM Support access is provisioned automatically</AlertTitle>
              A second Super Admin will be created at{' '}
              <strong>support+{previewSlug || '<CompanyName>'}@pomsafe.com</strong>{' '}
              so CS can sign into this org directly.{' '}
              {isIraStandalone
                ? 'The organization starts with Risk Assessment enabled and every other feature off.'
                : 'Default geofence numbers and the RapidSOS response system are configured for you.'}
            </Alert>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <LoadingButton
                variant="outlined"
                color="inherit"
                size="large"
                onClick={() => navigate('/customers')}
                disabled={loading.create}
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                variant="contained"
                size="large"
                onClick={() => handleSubmit()}
                loading={loading.create}
              >
                Send Invitation
              </LoadingButton>
            </Stack>
          </Stack>
        </Form>
      </FormikProvider>
    </Card>
  );
};
